import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
} from '@mui/material';
import {
  People as PeopleIcon,
  Group as GroupIcon,
  Home as HomeIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  Notifications as NotificationsIcon,
  School as SchoolIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { 
  trackUIInteraction, 
  trackAPICall, 
  addBreadcrumb, 
  logger, 
  setContext,
  setTag 
} from '../utils/sentryUtils';
import { useSentryTracking } from './SentryComponentWrapper';

const Dashboard = ({ currentUser }) => {
  const [stats, setStats] = useState({
    totalUsers: 156,
    activeUsers: 142,
    totalGroups: 8,
    totalHouseholds: 45,
    totalRoles: 12,
    seekingTeamMembers: 23,
  });
  const [loading, setLoading] = useState(false);
  
  // Initialize Sentry tracking for this component
  useSentryTracking('Dashboard', { includeUpdates: true, includeRender: true });
  
  useEffect(() => {
    // Set user context for this dashboard session
    if (currentUser) {
      setContext("dashboard", {
        userId: currentUser.id,
        userRole: currentUser.role,
        userEmail: currentUser.email,
        accessTime: new Date().toISOString()
      });
      
      setTag("dashboard.user_role", currentUser.role);
      setTag("dashboard.access_type", "crew");
      
      addBreadcrumb("Dashboard accessed", "navigation", {
        userId: currentUser.id,
        userRole: currentUser.role,
        route: "crew/dashboard"
      });
      
      logger.info("User accessed crew dashboard", {
        userId: currentUser.id,
        userRole: currentUser.role,
        userEmail: currentUser.email
      });
    }
    
    // Load dashboard data
    loadDashboardData();
  }, [currentUser]);
  
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Track the data loading operation
      await trackAPICall(
        "GET",
        "/api/dashboard/stats",
        async () => {
          // Simulate API call - replace with actual API call
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Mock response - replace with actual API response
          return {
            totalUsers: 156,
            activeUsers: 142,
            totalGroups: 8,
            totalHouseholds: 45,
            totalRoles: 12,
            seekingTeamMembers: 23,
          };
        },
        {
          userId: currentUser?.id,
          dashboardType: "crew"
        }
      );
      
      logger.info("Dashboard data loaded successfully", {
        userId: currentUser?.id,
        dataSource: "api"
      });
      
    } catch (error) {
      logger.error("Failed to load dashboard data", {
        userId: currentUser?.id,
        error: error.message,
        dashboardType: "crew"
      });
    } finally {
      setLoading(false);
    }
  };

  const recentActivities = [
    { id: 1, text: 'New user registration: John Smith', time: '2 hours ago', type: 'user' },
    { id: 2, text: 'Role assignment: Coach role added to Sarah Johnson', time: '4 hours ago', type: 'role' },
    { id: 3, text: 'Group update: FRC team roster updated', time: '6 hours ago', type: 'group' },
    { id: 4, text: 'Household created: Johnson Family', time: '1 day ago', type: 'household' },
  ];

  const quickActions = [
    { title: 'Add New User', description: 'Register a new team member', icon: <PeopleIcon />, action: 'add_user' },
    { title: 'Create Group', description: 'Set up a new team or program', icon: <GroupIcon />, action: 'create_group' },
    { title: 'Assign Roles', description: 'Manage user permissions', icon: <AssignmentIcon />, action: 'assign_roles' },
    { title: 'Generate Roster', description: 'Export team rosters', icon: <SchoolIcon />, action: 'generate_roster' },
  ];

  const handleQuickAction = (action) => {
    trackUIInteraction(
      `Quick Action: ${action}`,
      () => {
        addBreadcrumb("Quick action clicked", "ui.interaction", {
          action: action,
          userId: currentUser?.id,
          userRole: currentUser?.role
        });
        
        logger.info("Quick action clicked", {
          action: action,
          userId: currentUser?.id,
          userRole: currentUser?.role
        });
        
        // Handle the action (navigation, modal opening, etc.)
        console.log(`Handling quick action: ${action}`);
      },
      {
        actionType: "quick_action",
        action: action,
        component: "Dashboard",
        userId: currentUser?.id
      }
    );
  };

  const StatCard = ({ title, value, icon, color = 'primary' }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="text.secondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div" color={color}>
              {value}
            </Typography>
          </Box>
          <Box sx={{ color: `${color}.main` }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Crew Dashboard Overview
      </Typography>
      
      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            icon={<PeopleIcon sx={{ fontSize: 40 }} />}
            color="primary"
          />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <StatCard
            title="Active Users"
            value={stats.activeUsers}
            icon={<TrendingUpIcon sx={{ fontSize: 40 }} />}
            color="success"
          />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <StatCard
            title="Groups"
            value={stats.totalGroups}
            icon={<GroupIcon sx={{ fontSize: 40 }} />}
            color="secondary"
          />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <StatCard
            title="Households"
            value={stats.totalHouseholds}
            icon={<HomeIcon sx={{ fontSize: 40 }} />}
            color="info"
          />
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid xs={12}>
          <Typography variant="h5" gutterBottom>
            Quick Actions
          </Typography>
        </Grid>
        {quickActions.map((action, index) => (
          <Grid xs={12} sm={6} md={3} key={index}>
            <Card 
              sx={{ 
                height: '100%', 
                cursor: 'pointer',
                '&:hover': { boxShadow: 3 }
              }}
              onClick={() => handleQuickAction(action.action)}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <Box sx={{ mb: 2, color: 'primary.main' }}>
                  {action.icon}
                </Box>
                <Typography variant="h6" gutterBottom>
                  {action.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {action.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Recent Activities */}
      <Grid container spacing={3}>
        <Grid xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>
              Recent Activities
            </Typography>
            <List>
              {recentActivities.map((activity) => (
                <ListItem key={activity.id} divider>
                  <ListItemIcon>
                    {activity.type === 'user' && <PeopleIcon />}
                    {activity.type === 'role' && <AssignmentIcon />}
                    {activity.type === 'group' && <GroupIcon />}
                    {activity.type === 'household' && <HomeIcon />}
                  </ListItemIcon>
                  <ListItemText
                    primary={activity.text}
                    secondary={activity.time}
                  />
                  <Chip 
                    label={activity.type} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
        
        <Grid xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>
              System Status
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="success.main">
                ✓ All systems operational
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Last updated: {new Date().toLocaleTimeString()}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard; 